import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isSessionReplayExperimentEnabled } from "../../experiment/experiments";
import { logError } from "../../utils/logger";
import { initializeSessionReplay } from "./initializeSessionReplay";
import { sessionReplayDocumentState } from "./sessionReplayDocumentState";
import { registerSessionReplayFlushAttachmentSync } from "./sessionReplayFlushSyncRegistry";
import { isInstalledWithinSessionReplayWindow } from "./sessionReplayEligibility";
import { subscribeSessionReplayExperiment } from "./sessionReplayLifecycle";

/** Serialized reconcile so concurrent ticks (Strict Mode, poll + subscription) do not race. */
let reconcileTail: Promise<void> = Promise.resolve();

/**
 * Poll interval for install-window eligibility only (experiment changes use Statsig subscription).
 * 60s balances picking up “installed just over an hour ago” edge cases without waking the tab often
 * for battery / background-tab cost. Kept as a local const unless we need env- or experiment-driven tuning.
 */
const SESSION_REPLAY_CONDITION_POLL_MS = 60_000;

export const __resetSessionReplayInitializerForTests = (): void => {
  sessionReplayDocumentState.reset();
  reconcileTail = Promise.resolve();
};

async function runSessionReplayReconcileSafe(): Promise<void> {
  try {
    await runSessionReplayReconcile();
  } catch (error: unknown) {
    logError({ error, impact: "Session replay eligibility reconcile failed" });
  }
}

/** Chains work on `reconcileTail` so ticks never overlap; the work itself uses async/await in {@link runSessionReplayReconcileSafe}. */
function enqueueSessionReplayReconcile(): Promise<void> {
  reconcileTail = reconcileTail.then(() => runSessionReplayReconcileSafe());
  return reconcileTail;
}

async function runSessionReplayReconcile(): Promise<void> {
  const eligible = (await isInstalledWithinSessionReplayWindow()) && isSessionReplayExperimentEnabled();

  if (eligible && !sessionReplayDocumentState.isInitialized) {
    await initializeSessionReplay();
    sessionReplayDocumentState.markInitialized();
  }
  // While the same Gas session id is active, keep recording even if experiment/install window
  // later become false. When Gas mints a new session id, `onAnalyticsWebClientSessionRotation`
  // flushes and either updates Amplitude's session or shuts replay down if no longer eligible.
}

/**
 * Starts session replay only when the experiment is enabled and install is within the last hour
 * (see `sessionReplayEligibility`). Re-checks on Statsig updates and a periodic poll so users who
 * become eligible after load still start recording. After recording has started once, it is not
 * stopped when those conditions later become false until the analytics client starts a new browser
 * session; then replay follows the new session or stops if the user is ineligible.
 *
 * Flushes replay events on route change and pagehide so sessions are sent to Amplitude
 * when the user navigates to options or closes the tab (extension NTP can unload).
 */
export const SessionReplayInitializer = (): null => {
  const flushAttachedRef = useRef(false);
  const removePagehideRef = useRef<(() => void) | undefined>(undefined);
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    let mounted = true;

    const detachFlushListeners = (): void => {
      removePagehideRef.current?.();
      removePagehideRef.current = undefined;
      flushAttachedRef.current = false;
    };

    const attachFlushListeners = async (): Promise<void> => {
      if (flushAttachedRef.current || !sessionReplayDocumentState.isInitialized) {
        return;
      }
      // Named async chunk for session-replay (see `initializeSessionReplay.ts` module comment: Atlaspack + `webpackChunkName`).
      const { flush } = await import(
        /* webpackChunkName: "amplitude-session-replay" */ "@amplitude/session-replay-browser"
      );
      const doFlush = (): void => {
        void flush(true);
      };
      window.addEventListener("pagehide", doFlush);
      removePagehideRef.current = (): void => window.removeEventListener("pagehide", doFlush);
      flushAttachedRef.current = true;
    };

    const syncFlushAttachment = async (): Promise<void> => {
      if (sessionReplayDocumentState.isInitialized && !flushAttachedRef.current) {
        await attachFlushListeners();
      } else if (!sessionReplayDocumentState.isInitialized && flushAttachedRef.current) {
        detachFlushListeners();
      }
    };

    const unregisterFlushAttachmentSync = registerSessionReplayFlushAttachmentSync(() => {
      void syncFlushAttachment();
    });

    const tick = async (): Promise<void> => {
      await enqueueSessionReplayReconcile();
      if (!mounted) {
        return;
      }
      await syncFlushAttachment();
    };

    void tick();

    const unsubscribeExperiment = subscribeSessionReplayExperiment(() => {
      void tick();
    });

    const pollId = window.setInterval(() => {
      void tick();
    }, SESSION_REPLAY_CONDITION_POLL_MS);

    return (): void => {
      mounted = false;
      unregisterFlushAttachmentSync();
      unsubscribeExperiment();
      window.clearInterval(pollId);
      detachFlushListeners();
      // NTP mounts once per tab today; if this component were reused in a mount/unmount loop, this
      // cleanup must stay in sync with every timer and listener above.
    };
  }, []);

  // Flush when user navigates within the app (e.g. NTP → options) so events are sent before the view changes.
  useEffect(() => {
    if (!flushAttachedRef.current || prevPathnameRef.current === location.pathname) {
      return;
    }
    prevPathnameRef.current = location.pathname;
    // Same async chunk — see `initializeSessionReplay.ts` module comment.
    void import(/* webpackChunkName: "amplitude-session-replay" */ "@amplitude/session-replay-browser").then(
      ({ flush }) => flush(true),
    );
  }, [location.pathname]);

  return null;
};
